import { memo, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BrandStatsResponse } from "services/api/dashboard/types";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import CardHeader from "../../../../common/CardHeader";
import { text } from "../../../../common/dashboardPalette";

interface VehicleStatusCardProps {
  data: BrandStatsResponse;
}

// Multi-series paleta cyclički — modeli različitih boja unutar jedne marke
// (sky / indigo / teal / amber / rose) prati globalnu dashboard paletu.
const MODEL_COLORS = [
  "bg-sky-500",
  "bg-indigo-500",
  "bg-teal-500",
  "bg-amber-500",
  "bg-rose-500",
];

// Blage (poluprozirne) ravne boje segmenata — bez gradijenta. Pune klase za Tailwind JIT.
const MODEL_FILLS = [
  "bg-sky-500/60",
  "bg-indigo-500/60",
  "bg-teal-500/60",
  "bg-amber-500/60",
  "bg-rose-500/60",
];

/** Vozila po marki — horizontal stacked bar, dashboard paleta. */
const VehicleStatusCard: React.FC<VehicleStatusCardProps> = ({ data }) => {
  const navigate = useNavigate();
  const brands = data?.brands ?? [];
  const total = data?.total ?? 0;
  const [hoveredBrand, setHoveredBrand] = useState<string | null>(null);
  const maxCount = useMemo(() => Math.max(...brands.map((b) => b.total), 1), [brands]);

  // Klik na marku -> pretraga vozila filtrirana po tom proizvođaču.
  const handleBrandClick = (manufacturerId: string | null) => {
    const query = manufacturerId ? `?manufacturer=${manufacturerId}` : "";
    navigate(`${FleetVehicleRoutes.FleetVehicleSearchPageRouter}${query}`);
  };

  if (brands.length === 0) {
    return (
      <div className="rounded-sm px-4 py-5 bg-white h-full flex items-center justify-center">
        <span className="text-sm text-slate-400">Nema podataka</span>
      </div>
    );
  }

  return (
    <div className="rounded-sm pr-4 py-5 bg-white">
      <CardHeader
        title="Vozila po marki"
        subtitle="Top 10 proizvođača"
        right={<span className={`text-2xl font-bold ${text.primary}`}>{total}</span>}
      />

      <div className="space-y-2">
        {brands.map((brand) => {
          const widthPercent = maxCount > 0 ? (brand.total / maxCount) * 100 : 0;
          return (
            <div
              key={brand.brand}
              className="group/row relative flex items-center gap-3 rounded-sm ring-1 ring-slate-200 bg-white px-3 py-2.5 cursor-pointer transition-all hover:ring-2 hover:ring-sky-400 hover:shadow-sm"
              onMouseEnter={() => setHoveredBrand(brand.brand)}
              onMouseLeave={() => setHoveredBrand(null)}
              onClick={() => handleBrandClick(brand.id)}
            >
              {brand.image && (
                <img src={brand.image} alt={brand.brand} className="w-10 h-10 object-contain flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-sm font-semibold text-slate-800 truncate">{brand.brand}</span>
                  <span className="text-sm font-bold text-slate-800 flex-shrink-0">
                    {brand.total}
                  </span>
                </div>
                <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100">
                  {brand.models.map((model, j) => {
                    const modelPercent = brand.total > 0 ? (model.count / brand.total) * widthPercent : 0;
                    return (
                      <div
                        key={model.model}
                        className={`h-full ${MODEL_FILLS[j % MODEL_FILLS.length]}`}
                        style={{ width: `${modelPercent}%` }}
                      />
                    );
                  })}
                </div>
              </div>
              {hoveredBrand === brand.brand && (
                <div className="absolute left-0 bottom-full mb-1 z-20 min-w-[180px] bg-slate-800 text-white text-sm rounded-lg shadow-xl p-3 pointer-events-none">
                  <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">
                    {brand.brand} · {brand.total} vozila
                  </div>
                  {brand.models.map((m, j) => (
                    <div key={m.model} className="flex justify-between text-sm mb-1 gap-4 items-center">
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <span className={`w-2 h-2 rounded-sm ${MODEL_COLORS[j % MODEL_COLORS.length]}`} />
                        <span className="text-slate-300">{m.model}</span>
                      </div>
                      <span className="font-medium whitespace-nowrap">{m.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default memo(VehicleStatusCard);
